import type { BaselineState, Hypothesis, HypothesisCreate, HypothesisUpdate, SourceClaim, ThresholdDirection, UpdateFrequency } from '../api/useHypotheses.ts';

/**
 * Skjemaet slik det står i minnet mens det skrives (H2.2). Tallene er TEKST her — det
 * personen skrev, også «41,3» og en halvskrevet «4» — og blir tall først i kroppen til
 * tjeneren (`tilKropp`). Uten nett står alt dette i minnet til fanen lukkes; ingenting
 * skrives til nettleserens lagring (K8, «Ingen offline» i H2.2).
 */
export interface Skjema {
  title: string;
  change: string;
  audience: string;
  effect: string;
  metricName: string;
  sourceText: string;
  /** H3.3: en kilde fra katalogen («Bruk denne kilden»). Da er sourceText, sourceClaim og updateFrequency tomme. */
  metricSourceId?: string;
  sourceClaim?: SourceClaim;
  updateFrequency?: UpdateFrequency;
  baselineState?: BaselineState;
  baselineValue: string;
  /** `plainDate` som «ÅÅÅÅ-MM-DD», tom til den er satt. */
  baselineOn: string;
  direction?: ThresholdDirection;
  threshold: string;
  unit: string;
  testEndsOn: string;
  whatWeLose: string;
  background: string;
  /** H3.3, «Min er en annen»: den tidligere hypotesen innsenderen sier hennes er annerledes enn. */
  supersedesId?: string;
  /** «Hva er annerledes med din?» — krever pekeren, og pekeren krever den, ved «Send inn». */
  differenceNote: string;
}

export const TOMT: Skjema = {
  title: '',
  change: '',
  audience: '',
  effect: '',
  metricName: '',
  sourceText: '',
  baselineValue: '',
  baselineOn: '',
  threshold: '',
  unit: '',
  testEndsOn: '',
  whatWeLose: '',
  background: '',
  differenceNote: '',
};

export type Felt = keyof Skjema;

/** Kontraktens maksgrenser (`HypothesisForm`). Minimumene er leietakerens og står i porter.ts. */
export const MAKS: Partial<Record<Felt, number>> = {
  title: 80,
  change: 280,
  audience: 140,
  effect: 280,
  metricName: 140,
  sourceText: 140,
  unit: 20,
  background: 1000,
  whatWeLose: 280,
  differenceNote: 280,
};

export type LeddNoekkel = 'tittel' | 'endring' | 'effekt' | 'maaltall' | 'kilde' | 'idag' | 'rett' | 'venter' | 'hvorfor';

export interface Ledd {
  k: LeddNoekkel;
  /** Spørsmålet — leddets overskrift og radens navn. */
  sp: string;
  felt: Felt[];
  /** Spørsmålets egen hjelpelinje, der leietakerens tips ikke får plass (under 900). */
  hjelp: string;
  valgfri?: boolean;
}

/**
 * Leddene i fast rekkefølge (`hypotese-innsending.jsx`, LEDD). Ingen nummer og ingen «1 av 9»:
 * setningen er framdriften. `k` er også adressens siste ledd (/h/utkast/{id}/{k}).
 */
export const LEDD: Ledd[] = [
  { k: 'tittel', sp: 'Hva heter den?', felt: ['title'], hjelp: 'Kort nok til å stå i en liste.' },
  { k: 'endring', sp: 'Hva vil du endre, og for hvem?', felt: ['change', 'audience'], hjelp: 'Én endring og én gruppe.' },
  { k: 'effekt', sp: 'Hva tror du skjer?', felt: ['effect'], hjelp: 'Det du venter at endringen gjør.' },
  { k: 'maaltall', sp: 'Hvordan ser vi at det virker?', felt: ['metricName'], hjelp: 'Et tall som kan gå opp eller ned.' },
  { k: 'kilde', sp: 'Hvor kommer tallet fra?', felt: ['sourceText', 'metricSourceId', 'sourceClaim', 'updateFrequency'], hjelp: 'Der tallet finnes i dag.' },
  { k: 'idag', sp: 'Hva er tallet i dag?', felt: ['baselineState', 'baselineValue', 'baselineOn'], hjelp: 'Tallet og datoen det gjelder for.' },
  { k: 'rett', sp: 'Når har vi rett?', felt: ['direction', 'threshold', 'unit', 'testEndsOn'], hjelp: 'Tallet som avgjør, og datoen.' },
  { k: 'venter', sp: 'Mister vi noe om vi venter?', felt: ['whatWeLose'], hjelp: 'Valgfritt.', valgfri: true },
  { k: 'hvorfor', sp: 'Hvorfor tror du det?', felt: ['background'], hjelp: 'Valgfritt.', valgfri: true },
];

export const LES_OVER = 'les-over';

export const leddFor = (felt: Felt): LeddNoekkel => LEDD.find((l) => l.felt.includes(felt))!.k;

export const erLedd = (k: string | undefined): k is LeddNoekkel => LEDD.some((l) => l.k === k);

/**
 * Tallet slik personen skrev det: komma eller punktum, mellomrom rundt. Alt annet — også
 * «46 %» — er ikke et tall, og da står teksten i feltet og feltet sier det.
 */
export function lesTall(tekst: string): number | undefined {
  const t = tekst.trim();
  if (!/^-?\d+([.,]\d+)?$/.test(t)) return undefined;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Lim inn «41,3 %»: tallet og en enhet etter det. Brukes bare når enheten er tom, og
 * skjemaet sier det i én linje — aldri stille.
 */
export function delTallOgEnhet(tekst: string): { tall: string; enhet: string } | null {
  const m = /^\s*(-?\d+(?:[.,]\d+)?)\s*(\S.{0,19})\s*$/.exec(tekst);
  if (!m || lesTall(m[1]!) === undefined) return null;
  return { tall: m[1]!, enhet: m[2]!.trim() };
}

const tekstFra = (v: string | undefined) => v ?? '';
const tallFra = (v: number | undefined) => (v === undefined ? '' : String(v).replace('.', ','));

/** Tjenerens hypotese som skjema — ved åpning av et utkast og når personen velger den lagrede. */
export function fraHypotese(h: Hypothesis): Skjema {
  return {
    title: tekstFra(h.title),
    change: tekstFra(h.change),
    audience: tekstFra(h.audience),
    effect: tekstFra(h.effect),
    metricName: tekstFra(h.metricName),
    sourceText: tekstFra(h.sourceText),
    metricSourceId: h.metricSource?.id,
    sourceClaim: h.sourceClaim,
    updateFrequency: h.updateFrequency,
    baselineState: h.baselineState,
    baselineValue: tallFra(h.baselineValue),
    baselineOn: tekstFra(h.baselineOn),
    direction: h.direction,
    threshold: tallFra(h.threshold),
    unit: tekstFra(h.unit),
    testEndsOn: tekstFra(h.testEndsOn),
    whatWeLose: tekstFra(h.whatWeLose),
    background: tekstFra(h.background),
    supersedesId: h.supersedes?.id,
    differenceNote: tekstFra(h.differenceNote),
  };
}

/**
 * Kroppen til `POST` (første lagring). Tekst sendes som den står — en tom streng tømmer feltet,
 * og tjeneren trimmer. Et tall som ikke er et tall, en tom dato og et valg som ikke er gjort,
 * utelates: et nytt utkast har ingenting å tømme.
 */
export function tilKropp(s: Skjema): HypothesisCreate {
  const kropp: HypothesisCreate = {
    title: s.title,
    change: s.change,
    audience: s.audience,
    effect: s.effect,
    metricName: s.metricName,
    sourceText: s.sourceText,
    unit: s.unit,
    whatWeLose: s.whatWeLose,
    background: s.background,
  };
  if (s.metricSourceId) kropp.metricSourceId = s.metricSourceId;
  if (s.supersedesId) kropp.supersedesId = s.supersedesId;
  if (s.differenceNote.trim()) kropp.differenceNote = s.differenceNote;
  if (s.sourceClaim) kropp.sourceClaim = s.sourceClaim;
  if (s.updateFrequency) kropp.updateFrequency = s.updateFrequency;
  if (s.baselineState) kropp.baselineState = s.baselineState;
  if (s.direction) kropp.direction = s.direction;
  const terskel = lesTall(s.threshold);
  if (terskel !== undefined) kropp.threshold = terskel;
  const baseline = lesTall(s.baselineValue);
  if (baseline !== undefined) kropp.baselineValue = baseline;
  if (s.baselineOn) kropp.baselineOn = s.baselineOn;
  if (s.testEndsOn) kropp.testEndsOn = s.testEndsOn;
  return kropp;
}

/**
 * Kroppen til `PATCH` (autolagring), uten `baseVersion`. Merge-patch (kontrakten 2026-09-27):
 * utelatt = «ikke rør», `null` = «tøm». Skjemaet er fasit for utkastet, så et tall, en dato
 * eller et valg som står tomt, sendes som `null` — ellers ville tjeneren beholdt verdien
 * personen tømte, og skjema og tjener ble ulike. Et tall som ikke er et tall ennå («4,»),
 * utelates fortsatt: halvskrevet tekst tømmer aldri det som er lagret.
 */
export function tilPatch(s: Skjema): Omit<HypothesisUpdate, 'baseVersion'> {
  const kropp: Omit<HypothesisUpdate, 'baseVersion'> = {
    ...tilKropp(s),
    // H3.3: «Bytt kilde» er null; «Min er en annen» tømmes med null og tom streng. Endringsskjemaet
    // sender bare feltene som er endret, så pekeren går aldri med etter «Send inn» (409 wording_locked).
    metricSourceId: s.metricSourceId ?? null,
    supersedesId: s.supersedesId ?? null,
    differenceNote: s.differenceNote,
    sourceClaim: s.sourceClaim ?? null,
    updateFrequency: s.updateFrequency ?? null,
    baselineState: s.baselineState ?? null,
    direction: s.direction ?? null,
    baselineOn: s.baselineOn || null,
    testEndsOn: s.testEndsOn || null,
  };
  const tall = (tekst: string): number | null | undefined => (tekst.trim() === '' ? null : lesTall(tekst));
  const terskel = tall(s.threshold);
  if (terskel === undefined) delete kropp.threshold;
  else kropp.threshold = terskel;
  const baseline = tall(s.baselineValue);
  if (baseline === undefined) delete kropp.baselineValue;
  else kropp.baselineValue = baseline;
  return kropp;
}

/** Feltene der to versjoner av skjemaet er ulike, i leddenes rekkefølge. */
export function ulikeFelt(a: Skjema, b: Skjema): Felt[] {
  const norm = (f: Felt, s: Skjema) => {
    const v = s[f];
    if (f === 'threshold' || f === 'baselineValue') return String(lesTall(String(v ?? '')) ?? String(v ?? '').trim());
    return typeof v === 'string' ? v.trim() : (v ?? '');
  };
  return LEDD.flatMap((l) => l.felt).filter((f) => norm(f, a) !== norm(f, b));
}

/** Felt over kontraktens maksgrense: utkastet lagres ikke før de er kortere (teksten står). */
export function forLange(s: Skjema): Array<{ felt: Felt; over: number }> {
  return (Object.entries(MAKS) as Array<[Felt, number]>).flatMap(([f, maks]) => {
    // Utrimmet: tjeneren måler maksgrensen på kroppen slik den kommer, før den trimmer.
    const lengde = String(s[f] ?? '').length;
    return lengde > maks ? [{ felt: f, over: lengde - maks }] : [];
  });
}
